import { useEffect, useState } from "react";
import { Users, Loader2 } from "lucide-react";
import { CoordinatorLayout } from "@/components/layout/coordinator-layout";

const BASE = import.meta.env.BASE_URL.replace(/\/$/, "");

interface Row {
  userId: string;
  patientName: string | null;
  patientDob: string | null;
  nhsNumber: string | null;
  lastRequestAt: string;
  openCount: number;
  totalCount: number;
}

export default function CoordinatorPatients() {
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetch(`${BASE}/api/coordinator/patients`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then((d) => setRows(d as Row[]))
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  return (
    <CoordinatorLayout>
      <div className="p-8 max-w-6xl">
        <div className="flex items-center gap-3 mb-2">
          <Users className="h-6 w-6 text-sky-600" />
          <h1 className="text-2xl font-bold text-slate-900">Patients</h1>
        </div>
        <p className="text-slate-600 mb-6">
          Patients who have submitted requests through this practice. Non-clinical
          identifiers only — clinical history is not available in this portal.
        </p>

        {loading && (
          <div className="flex items-center gap-2 text-slate-500">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading…
          </div>
        )}
        {error && (
          <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2 mb-4">
            {error}
          </p>
        )}
        {!loading && !error && rows.length === 0 && (
          <p className="text-sm text-slate-500 bg-white border border-slate-200 rounded-2xl p-6 text-center">
            No patients have contacted this practice yet.
          </p>
        )}

        {rows.length > 0 && (
          <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-600 text-left">
                <tr>
                  <th className="px-4 py-3 font-medium">Name</th>
                  <th className="px-4 py-3 font-medium">DOB</th>
                  <th className="px-4 py-3 font-medium">NHS no.</th>
                  <th className="px-4 py-3 font-medium text-right">Open</th>
                  <th className="px-4 py-3 font-medium text-right">Total</th>
                  <th className="px-4 py-3 font-medium">Last contact</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {rows.map((r) => (
                  <tr key={r.userId} data-testid={`patient-row-${r.userId}`}>
                    <td className="px-4 py-3 font-medium text-slate-900">
                      {r.patientName ?? "—"}
                    </td>
                    <td className="px-4 py-3 text-slate-600">{r.patientDob ?? "—"}</td>
                    <td className="px-4 py-3 text-slate-600 font-mono text-xs">
                      {r.nhsNumber ?? "—"}
                    </td>
                    <td className="px-4 py-3 text-right text-slate-900">{r.openCount}</td>
                    <td className="px-4 py-3 text-right text-slate-600">{r.totalCount}</td>
                    <td className="px-4 py-3 text-slate-500">
                      {new Date(r.lastRequestAt).toLocaleDateString("en-GB")}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </CoordinatorLayout>
  );
}
